import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';

import pollQuery from './depotResetPoll.graphql';

const POLL_INTERVAL_MS = 3000;
// One hour at the interval above. A run still applying after that is a server
// problem the UI cannot resolve by waiting longer.
const MAX_ATTEMPTS = 1200;
// depotReset(id:) returns a bare null for an unknown id or another user's
// run, with no GraphQL error to catch. Two attempts only, to absorb
// read-replica lag on the first poll after a mutation.
const MAX_NULL_ATTEMPTS = 2;

// PLANNED/COMPLETED/FAILED are resting states, except immediately after
// applyDepotReset resolves: the row is still PLANNED (DepotResetApplyJob
// flips it to APPLYING async inside the worker). A caller that just kicked
// off that work passes its prior status as `pollWhileStatus` so the next
// (contractually stale) response doesn't read as already resting.
const RUNNING_STATUSES: DepotResetStatusEnum[] = ['PLANNING', 'APPLYING'];

export type PolledDepotReset = NonNullable<DepotResetPollQuery['depotReset']>;

export const usePollDepotReset = () => {
  const client = useApolloClient();
  const [depotReset, setDepotReset] = useState<PolledDepotReset | null>(null);
  const [pollFailed, setPollFailed] = useState(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const cancelledRef = useRef(false);
  // Each startPolling call claims a generation. cancelledRef alone cannot
  // retire a superseded chain, because starting a new one resets it to false --
  // which would let the old chain's pending timer and in-flight query resume
  // and run a second loop alongside the new one.
  const generationRef = useRef(0);

  useEffect(
    () => () => {
      cancelledRef.current = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  const startPolling = useCallback(
    (id: string, pollWhileStatus?: DepotResetStatusEnum) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      cancelledRef.current = false;
      setPollFailed(false);

      generationRef.current += 1;
      const generation = generationRef.current;
      const isStale = () =>
        cancelledRef.current || generationRef.current !== generation;

      let nullCount = 0;
      let attempts = 0;

      const scheduleNext = (tick: () => void) => {
        timeoutRef.current = setTimeout(tick, POLL_INTERVAL_MS);
      };

      const tick = async () => {
        /* istanbul ignore next -- @preserve: unreachable as written. Both
         * cancellation points (unmount cleanup, supersession above) clear the
         * pending timer, so no scheduled tick survives to start stale, and the
         * direct call below runs on a freshly claimed generation. Kept so a
         * future caller that schedules without clearing still can't run two
         * loops; the post-query check is the one that carries real traffic. */
        if (isStale()) return;

        if (attempts >= MAX_ATTEMPTS) {
          setPollFailed(true);
          return;
        }
        attempts += 1;

        try {
          const { data } = await client.query<
            DepotResetPollQuery,
            DepotResetPollQueryVariables
          >({
            query: pollQuery,
            variables: { id },
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
          });

          if (isStale()) return;

          const reset = data?.depotReset;

          if (!reset) {
            nullCount += 1;
            if (nullCount >= MAX_NULL_ATTEMPTS) {
              setPollFailed(true);
              return;
            }
            scheduleNext(tick);
            return;
          }

          nullCount = 0;
          setDepotReset(reset);

          if (
            RUNNING_STATUSES.includes(reset.status) ||
            reset.status === pollWhileStatus
          ) {
            scheduleNext(tick);
          }
        } catch {
          // A network blip must not abandon a run that is still applying
          // server-side, so transport errors simply retry on the next tick.
          /* istanbul ignore next -- @preserve: redundant with the top-of-tick
           * staleness check, which already retires a cancelled or superseded
           * poll before any scheduleNext call can be reached from here. Kept
           * only so a cancellation racing an in-flight rejected query doesn't
           * schedule one extra timer that would immediately no-op. */
          if (isStale()) return;
          scheduleNext(tick);
        }
      };

      tick();
    },
    [client],
  );

  // Retires the current chain and drops what it produced, for a caller sending
  // the user back to a pre-plan state. No generation bump needed: startPolling
  // claims a fresh one, and cancelledRef alone fails an in-flight isStale().
  const reset = useCallback(() => {
    cancelledRef.current = true;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setDepotReset(null);
    setPollFailed(false);
  }, []);

  return { startPolling, depotReset, pollFailed, reset };
};
